import { createContext, use, useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { claimsAreSuperUser } from '@shared';
import { api } from '../lib/api';
import type { ApiData } from '../lib/api';
import { createKeycloakAuth } from '../lib/keycloak';
import {
  clearUnauthorized,
  getClaims,
  getToken,
  getUserId,
  markUnauthorized,
  onTokenChange,
  refreshToken,
  setRefreshStrategy,
  setToken,
} from '../lib/token';

export type SessionUser = ApiData<typeof api.me.get>;

export type AuthMode = 'dev' | 'keycloak' | 'none';

const DEV_AUTH = process.env.BUN_PUBLIC_DEV_AUTH === '1';
const KEYCLOAK_ISSUER = process.env.BUN_PUBLIC_KEYCLOAK_ISSUER;
const KEYCLOAK_CLIENT_ID = process.env.BUN_PUBLIC_KEYCLOAK_CLIENT_ID;

const keycloak =
  !DEV_AUTH && KEYCLOAK_ISSUER && KEYCLOAK_CLIENT_ID ? createKeycloakAuth(KEYCLOAK_ISSUER, KEYCLOAK_CLIENT_ID) : null;

export const authMode: AuthMode = DEV_AUTH ? 'dev' : keycloak ? 'keycloak' : 'none';

async function mintDevTokenFor(userId: string, superUser: boolean) {
  const response = await fetch('/api/dev/token', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ userId, superUser }),
  });

  if (!response.ok) return null;

  const body = await response.json();

  return body.token ?? null;
}

function currentTokenIsSuperUser() {
  const claims = getClaims();

  return claims ? claimsAreSuperUser(claims) : false;
}

setRefreshStrategy(async () => {
  if (keycloak) return await keycloak.refresh();

  const userId = getUserId();

  return userId ? await mintDevTokenFor(userId, currentTokenIsSuperUser()) : null;
});

export async function completeKeycloakCallback() {
  if (!keycloak) throw new Error('Keycloak is not configured');

  const token = await keycloak.handleCallback();

  clearUnauthorized();
  setToken(token);
}

const unauthenticatedPaths = ['/auth/callback', '/invitations/'];

function isUnauthenticatedPath(pathname: string) {
  return unauthenticatedPaths.some((path) => pathname.startsWith(path));
}

export type AuthError = 'unauthorized';

type AuthState = {
  mode: AuthMode;
  token: string | null;
  user: SessionUser | null;
  isSuperUser: boolean;
  isLoading: boolean;
  error: AuthError | null;
  signInDev: (userId: string, superUser: boolean) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState(getToken());
  const [resolved, setResolved] = useState<{ token: string; user: SessionUser | null } | null>(null);
  const [error, setError] = useState<AuthError | null>(null);
  const hasRefreshedRef = useRef(false);
  const isSigningOutRef = useRef(false);

  useEffect(() => onTokenChange(setTokenState), []);

  useEffect(() => {
    if (!token) {
      if (keycloak && !error && !isSigningOutRef.current && !isUnauthenticatedPath(window.location.pathname)) {
        void keycloak.signIn();
      }

      return;
    }

    let cancelled = false;

    const rejectAsUnauthorized = () => {
      hasRefreshedRef.current = false;
      markUnauthorized();
      setError('unauthorized');
      setToken(null);
    };

    void api.me.get().then(async ({ data, error: apiError }) => {
      if (cancelled) return;

      if (apiError?.status === 401) {
        if (hasRefreshedRef.current) return rejectAsUnauthorized();

        hasRefreshedRef.current = true;

        const refreshed = await refreshToken();

        if (cancelled) return;
        if (!refreshed || refreshed === token) rejectAsUnauthorized();

        return;
      }

      hasRefreshedRef.current = false;
      setResolved({ token, user: data ?? null });
    });

    return () => {
      cancelled = true;
    };
  }, [token, error]);

  const user = token !== null && resolved?.token === token ? resolved.user : null;

  const signInDev = useCallback(async (userId: string, superUser: boolean) => {
    const minted = await mintDevTokenFor(userId, superUser);

    if (!minted) throw new Error('Failed to sign in as dev user');

    clearUnauthorized();
    setError(null);
    setToken(minted);
  }, []);

  const signOut = useCallback(() => {
    clearUnauthorized();
    setError(null);
    setToken(null);

    if (!keycloak) return;

    isSigningOutRef.current = true;
    void keycloak.signOut();
  }, []);

  return (
    <AuthContext
      value={{
        mode: authMode,
        token,
        user,
        isSuperUser: currentTokenIsSuperUser(),
        isLoading: token !== null && error === null && resolved?.token !== token,
        error,
        signInDev,
        signOut,
      }}
    >
      {children}
    </AuthContext>
  );
}

export function useAuth() {
  const auth = use(AuthContext);

  if (!auth) throw new Error('useAuth must be used within AuthProvider');

  return auth;
}

export function useCurrentUser() {
  const { user } = useAuth();

  if (!user) throw new Error('useCurrentUser requires a signed-in user');

  return user;
}
